import React, { useEffect } from 'react';

/**
 * A reusable modal component with Tailwind styling
 * 
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {function} props.onClose - Function to call when the modal is closed
 * @param {React.ReactNode} props.children - Content to display in the modal
 * @param {string} props.title - Optional title for the modal
 * @param {boolean} props.showCloseButton - Whether to show the close button (default: true)
 * @param {string} props.size - Size of the modal: 'sm', 'md', 'lg', 'xl' (default: 'md')
 */
const Modal = ({
    isOpen,
    onClose,
    children,
    title,
    showCloseButton = true,
    size = 'md'
}) => {
    // Prevent scrolling on the body when modal is open
    useEffect(() => {
        if (isOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = '';
        }

        return () => {
            document.body.style.overflow = '';
        };
    }, [isOpen]);

    if (!isOpen) return null;

    // Determine modal width based on size prop
    const sizeClasses = {
        sm: 'max-w-md',
        md: 'max-w-lg',
        lg: 'max-w-2xl',
        xl: 'max-w-4xl',
        full: 'max-w-full mx-4'
    };

    const modalSizeClass = sizeClasses[size] || sizeClasses.md;

    return (
        <div className="fixed inset-0 z-[99999] overflow-y-auto">
            <div className="fixed inset-0 bg-black bg-opacity-75 transition-opacity" onClick={onClose}></div>

            <div className="flex min-h-full items-center justify-center p-4 text-center sm:p-0">
                <div
                    className={`relative transform overflow-hidden rounded-lg bg-[#0D0D0D] text-left shadow-xl transition-all sm:my-8 w-full ${modalSizeClass} z-[100000]`}
                >
                    {(title || showCloseButton) && (
                        <div className="px-6 pt-5 pb-4 sm:p-6 flex justify-between items-center">
                            {title && <h3 className="text-2xl font-light">{title}</h3>}
                            {showCloseButton && (
                                <button
                                    onClick={onClose}
                                    className="text-gray-400 hover:text-white transition-colors"
                                >
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            )}
                        </div>
                    )}
                    <div className="px-6 pb-6 sm:px-6">
                        {children}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Modal; 